import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Spinner } from "../../../atoms/Spinner";
import moment from "moment";
import {
  HiOutlineUsers,
  HiTrash,
  HiShare,
  HiOutlineTrash,
} from "react-icons/hi";
import { HiOutlineUserPlus } from "react-icons/hi2";
import { FaArrowRight } from "react-icons/fa6";
import { toast } from "react-toastify";
import useAuth from "../../../../hooks/useAuth";
import {
  useGetSignupFormsList,
  useDeleteEntityPreferencesByPath,
} from "../../../../hooks/entity";
import { usePermissions, PlanTier } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { useGetCustomRegisterProcessId } from "../../../../hooks/users";
import { CustomButton } from "../../../atoms/CustomButton";
import { apiCache } from "../../../../utils/cacheManager";
import ShareModal from "../customRegistration/sections/ShareModal";
import UpgradeModal from "../../../modals/UpgradeModal";
import ConfirmModal from "../../../public/ConfirmModal";
import CollapsibleHowItWorks from "../../../common/CollapsibleHowItWorks";
import SignupFormsGettingStarted from "./SignupFormsGettingStarted";

interface SignupFormMeta {
  id: string;
  name: string;
  createdAt?: string;
}

export default function SignupFormsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const bookingTypeId = searchParams.get("booking_type");
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const { getPlanTier } = permissions;

  const [shareLink, setShareLink] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loadingShareId, setLoadingShareId] = useState<string | null>(null);

  const { mutateAsync: getCustomRegisterProcessId } =
    useGetCustomRegisterProcessId();

  const {
    data: signupFormsData,
    isLoading,
    refetch: refetchPrefs,
  } = useGetSignupFormsList(user.entity_identification);

  const { mutateAsync: deletePreferences } = useDeleteEntityPreferencesByPath();

  const allForms: SignupFormMeta[] = (signupFormsData?.forms ?? []).map(
    (form: any) => ({
      id: form.formId,
      name: form.name || t("signupForms.unnamedForm"),
      createdAt: form.createdAt,
    })
  );

  const planTier = getPlanTier();
  const formsLimit =
    planTier === PlanTier.PREMIUM
      ? 2
      : planTier === PlanTier.ENTERPRISE ||
        planTier === PlanTier.PROMO_ENTERPRISE_TRIAL
      ? 5
      : planTier === PlanTier.LARGE_BUSINESS
      ? Infinity
      : 1;
  const atLimit = formsLimit !== Infinity && allForms.length >= formsLimit;

  const upgradeTargetPlan =
    planTier === PlanTier.PREMIUM
      ? PlanTier.ENTERPRISE
      : planTier === PlanTier.ENTERPRISE ||
        planTier === PlanTier.PROMO_ENTERPRISE_TRIAL
      ? PlanTier.LARGE_BUSINESS
      : PlanTier.PREMIUM;

  const allUpgradeBenefits = [
    {
      id: "premium",
      tier: PlanTier.PREMIUM,
      text: t("signupForms.upgradeModal.benefit1"),
    },
    {
      id: "enterprise",
      tier: PlanTier.ENTERPRISE,
      text: t("signupForms.upgradeModal.benefit2"),
    },
    {
      id: "large",
      tier: PlanTier.LARGE_BUSINESS,
      text: t("signupForms.upgradeModal.benefit3"),
    },
  ];
  const tierRank: Record<string, number> = {
    [PlanTier.PREMIUM]: 1,
    [PlanTier.ENTERPRISE]: 2,
    [PlanTier.LARGE_BUSINESS]: 3,
  };
  const upgradeBenefits = allUpgradeBenefits
    .filter((b) => tierRank[b.tier] >= tierRank[upgradeTargetPlan])
    .map(({ id, text }) => ({ id, text }));

  const handleCreate = useCallback(() => {
    const existingIds = new Set(allForms.map((form) => form.id));
    const genHexId = () => {
      const bytes = new Uint8Array(2);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    };
    let formId = genHexId();
    while (existingIds.has(formId)) {
      formId = genHexId();
    }
    navigate(
      `/control-panel/signup-forms/${formId}${
        bookingTypeId
          ? `?booking_type=${encodeURIComponent(bookingTypeId)}`
          : ""
      }`,
      { replace: Boolean(bookingTypeId) }
    );
  }, [navigate, allForms, bookingTypeId]);

  const createdForBookingType = useRef(false);
  useEffect(() => {
    if (!bookingTypeId || isLoading || createdForBookingType.current) return;
    createdForBookingType.current = true;
    if (!permissions.can(Cap.SIGNUP_FORMS, Level.WRITE)) {
      setSearchParams({}, { replace: true });
    } else if (atLimit) {
      setSearchParams({}, { replace: true });
      setShowUpgradeModal(true);
    } else {
      handleCreate();
    }
  }, [
    bookingTypeId,
    isLoading,
    permissions,
    atLimit,
    handleCreate,
    setSearchParams,
  ]);

  const handleDelete = useCallback(
    async (formId: string) => {
      setDeletingId(formId);
      try {
        await deletePreferences({
          user_identification: customer_identification,
          entity_identification: user.entity_identification,
          path: `custom_registration.${formId}`,
        });
        await refetchPrefs();
        toast.success(t("signupForms.deleteSuccess"));
      } catch {
        toast.error(t("signupForms.deleteError"));
      } finally {
        setDeletingId(null);
      }
    },
    [deletePreferences, customer_identification, user, refetchPrefs, t]
  );

  const handleShare = useCallback(
    async (formId: string) => {
      setLoadingShareId(formId);
      try {
        const cacheKey = `custom_registration_process_id_${customer_identification}_${user.entity_identification}_${user.user_identification}_${formId}`;
        let pid = apiCache.get<string>(cacheKey);
        if (!pid) {
          const res = await getCustomRegisterProcessId({
            customer_identification,
            entity_identification: user.entity_identification,
            user_identification: user.user_identification,
            form_id: formId,
          });
          pid = res.data.element?.process_identification;
          if (pid) apiCache.setWithLongTTL(cacheKey, pid);
        }
        if (pid) {
          setShareLink(`${process.env.REACT_APP_HOST_URL}/process/${pid}`);
          setShowShareModal(true);
        }
      } catch {
        toast.error(t("signupForms.shareError"));
      } finally {
        setLoadingShareId(null);
      }
    },
    [customer_identification, user, getCustomRegisterProcessId, t]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-3 sm:p-4 md:p-6 max-w-4xl">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("signupForms.pageTitle")}
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("signupForms.pageDescription")}
          </p>
        </div>
        {allForms.length !== 0 &&
          permissions.can(Cap.SIGNUP_FORMS, Level.WRITE) && (
            <div className="flex items-center gap-2">
              {atLimit ? (
                <CustomButton
                  variant="primary"
                  title={t("signupForms.createButton") as string}
                  onClick={() => setShowUpgradeModal(true)}
                  icon={"plus"}
                  showUpgrade
                  upgradeText={t("signupForms.upgradeButton") as string}
                />
              ) : (
                <CustomButton
                  variant="primary"
                  title={t("signupForms.createButton") as string}
                  onClick={handleCreate}
                  icon={"plus"}
                />
              )}
            </div>
          )}
      </div>

      {allForms.length === 0 && (
        <>
          <SignupFormsGettingStarted />
          {permissions.can(Cap.SIGNUP_FORMS, Level.WRITE) && (
            <div className="flex flex-col items-center justify-center text-center mt-6">
              {atLimit ? (
                <CustomButton
                  variant="primary"
                  title={t("signupForms.gettingStarted.cta") as string}
                  onClick={() => setShowUpgradeModal(true)}
                  icon={"plus"}
                  showUpgrade
                  upgradeText={t("signupForms.upgradeButton") as string}
                />
              ) : (
                <CustomButton
                  variant="primary"
                  title={t("signupForms.gettingStarted.cta") as string}
                  onClick={handleCreate}
                  icon={"plus"}
                />
              )}
            </div>
          )}
        </>
      )}

      {allForms.length !== 0 && (
        <CollapsibleHowItWorks
          collapsible={true}
          title={t("signupForms.howItWorksToggle")}
          icon={<HiOutlineUserPlus className="text-blue-600" size={18} />}
          className="mb-4"
        >
          <SignupFormsGettingStarted />
        </CollapsibleHowItWorks>
      )}

      {allForms.length !== 0 && (
        <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
          {allForms.map((form) => (
            <div
              key={form.id}
              className="flex items-start justify-between gap-4 p-4"
            >
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-50">
                  <HiOutlineUsers size={18} className="text-gray-600" />
                </div>
                <div className="min-w-0">
                  <Link
                    to={`/control-panel/signup-forms/${form.id}`}
                    title={form.name}
                    className="group flex min-w-0 items-center gap-1 text-sm font-semibold text-gray-900 transition-colors hover:text-blue-700"
                  >
                    <span className="truncate">{form.name}</span>
                    <FaArrowRight className="h-2.5 w-2.5 shrink-0 text-gray-300 transition-colors group-hover:text-blue-700" />
                  </Link>
                  <dl className="mt-1.5 grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                    <dt className="text-gray-500">
                      {t("dashboard.usersList.item-15")}
                    </dt>
                    <dd className="truncate font-mono text-gray-700">
                      {form.id}
                    </dd>
                    {form.createdAt && (
                      <>
                        <dt className="text-gray-500">
                          {t("signupForms.createdAtLabel")}
                        </dt>
                        <dd className="text-gray-700">
                          {moment(form.createdAt).format("ll")}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-0.5">
                <CustomButton
                  variant="none"
                  size="icon"
                  onClick={() => handleShare(form.id)}
                  disabled={loadingShareId === form.id}
                  isLoading={loadingShareId === form.id}
                  className="rounded-md text-gray-400 transition-colors enabled:hover:text-blue-600"
                  tooltip={t("signupForms.shareTooltip") as string}
                  icon={<HiShare size={18} />}
                />
                {permissions.can(Cap.SIGNUP_FORMS, Level.DELETE) && (
                  <CustomButton
                    variant="none"
                    size="icon"
                    onClick={() => setPendingDeleteId(form.id)}
                    disabled={deletingId === form.id}
                    isLoading={deletingId === form.id}
                    className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                    tooltip={t("signupForms.deleteTooltip") as string}
                    icon={<HiOutlineTrash size={18} />}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        show={
          !!pendingDeleteId && permissions.can(Cap.SIGNUP_FORMS, Level.DELETE)
        }
        onClose={() => setPendingDeleteId(null)}
        title={t("signupForms.deleteConfirm.title")}
        description={t("signupForms.deleteConfirm.description", {
          name: allForms.find((f) => f.id === pendingDeleteId)?.name ?? "",
        })}
        cancelBtnName={t("common.cancel")}
        cancelBtnFn={() => setPendingDeleteId(null)}
        confirmBtnName={t("common.delete")}
        confirmBtnFn={() => {
          if (pendingDeleteId) {
            handleDelete(pendingDeleteId).then(() => setPendingDeleteId(null));
          }
        }}
        isLoading={!!deletingId}
        ConfirmBtnColor="red"
        icon={<HiTrash size={24} className="text-red-500" />}
        iconBgColor="bg-red-100"
      />

      <ShareModal
        showShareModal={showShareModal}
        setShowShareModal={setShowShareModal}
        processId=""
        generatedLink={shareLink}
      />

      <UpgradeModal
        showModal={showUpgradeModal}
        setShowModal={setShowUpgradeModal}
        targetPlan={upgradeTargetPlan}
        title={t("signupForms.upgradeModal.title") as string}
        description={t("signupForms.upgradeModal.description") as string}
        benefits={upgradeBenefits}
        iconTheme="yellow"
        upgradeText={t("signupForms.upgradeModal.cta") as string}
      />
    </div>
  );
}
